import { useState, useMemo, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type OzonCompetitor, type OzonStrategyCreatePayload } from '../api';
import { useAuth } from '../auth';

export function OzonStrategiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();
  const strategies = useQuery({ queryKey: ['ozon-strategies'], queryFn: api.ozonStrategies });
  const competitors = useQuery({ queryKey: ['ozon-competitors'], queryFn: api.ozonCompetitors });

  const [editing, setEditing] = useState<string | null>(null); // strategy id или 'new'
  const [expanded, setExpanded] = useState<string | null>(null); // показать продукты

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => api.ozonStrategyToggle(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ozon-strategies'] }),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.ozonStrategyDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ozon-strategies'] }),
  });

  return (
    <>
      <div className="page-header">
        <h2>Ozon Pricing Strategies <span className="muted" style={{ fontSize: 13, fontWeight: 'normal' }}>— встроенный репрайсер Ozon</span></h2>
        {isAdmin && (
          <button className="btn primary" onClick={() => setEditing('new')}>+ Создать Ozon-стратегию</button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16, background: '#f0f9ff' }}>
        <p style={{ margin: 0, fontSize: 13 }}>
          В отличие от наших стратегий (которые применяют свою логику и вызывают <code>import/prices</code>), <strong>Ozon Pricing Strategies</strong> работают на стороне Ozon: вы выбираете конкурента (например, market.yandex.ru) и коэффициент (например, 0.95 = быть на 5% дешевле), Ozon сам мониторит и применяет цену. Ничего из нашей логики (правил, метрик, breakeven) тут не используется.
        </p>
      </div>

      {editing && (
        <StrategyForm
          id={editing === 'new' ? null : editing}
          competitors={competitors.data?.competitor ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['ozon-strategies'] }); setEditing(null); }}
        />
      )}

      {strategies.isLoading && <div className="card">Загрузка…</div>}
      {strategies.data && (
        <table className="products">
          <thead>
            <tr>
              <th>Название</th>
              <th>Тип</th>
              <th>Конкуренты</th>
              <th className="num">Товаров</th>
              <th>Статус</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {strategies.data.strategies.length === 0 && (
              <tr><td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 24 }}>Стратегий нет.</td></tr>
            )}
            {strategies.data.strategies.map((s) => (
              <>
                <tr key={s.id}>
                  <td><strong>{s.name}</strong></td>
                  <td><span className="muted" style={{ fontSize: 12 }}>{s.type === 'MIN_EXT_PRICE' ? 'Системная (мин. внешний)' : 'Пользовательская'}</span></td>
                  <td className="num">{s.competitors_count ?? '—'}</td>
                  <td className="num">{s.products_count}</td>
                  <td>
                    <label style={{ cursor: isAdmin ? 'pointer' : 'default' }}>
                      <input type="checkbox" checked={s.enabled} disabled={!isAdmin || toggle.isPending} onChange={(e) => toggle.mutate({ id: s.id, enabled: e.target.checked })} />
                      {' '}{s.enabled ? <span style={{ color: 'var(--green)' }}>Активна</span> : <span className="muted">Выкл</span>}
                    </label>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>Товары</button>
                      {isAdmin && s.type === 'COMP_PRICE' && <button className="btn" onClick={() => setEditing(s.id)}>Изменить</button>}
                      {isAdmin && s.type === 'COMP_PRICE' && <button className="btn" onClick={() => { if (confirm(`Удалить "${s.name}"?`)) del.mutate(s.id); }}>Удалить</button>}
                      {s.type === 'MIN_EXT_PRICE' && <span className="muted" style={{ fontSize: 11, alignSelf: 'center' }}>системная</span>}
                    </div>
                  </td>
                </tr>
                {expanded === s.id && (
                  <tr key={`${s.id}-products`}>
                    <td colSpan={6} style={{ background: '#fafbfc' }}>
                      <StrategyProducts strategyId={s.id} isAdmin={isAdmin} />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function StrategyForm({ id, competitors, onClose, onSaved }: { id: string | null; competitors: OzonCompetitor[]; onClose: () => void; onSaved: () => void }) {
  const isNew = !id;
  const existing = useQuery({ queryKey: ['ozon-strategy', id], queryFn: () => api.ozonStrategyInfo(id!), enabled: !isNew });

  const [name, setName] = useState('');
  const [descr, setDescr] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [rules, setRules] = useState<{ competitor_id: number; coefficient: number }[]>([]);
  const [initialized, setInitialized] = useState(false);

  if (!isNew && existing.data && !initialized) {
    setName(existing.data.strategy_name);
    setDescr(existing.data.strategy_descr ?? '');
    setEnabled(existing.data.enabled);
    setRules(existing.data.competitors);
    setInitialized(true);
  }

  const save = useMutation({
    mutationFn: async (): Promise<{ ok: true }> => {
      const payload: OzonStrategyCreatePayload = { strategy_name: name, strategy_descr: descr || undefined, enabled, competitors: rules };
      if (isNew) await api.ozonStrategyCreate(payload);
      else await api.ozonStrategyUpdate(id!, payload);
      return { ok: true };
    },
    onSuccess: onSaved,
  });

  const onSubmit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };

  return (
    <div className="card" style={{ marginBottom: 16, border: '2px solid var(--accent)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{isNew ? 'Новая Ozon-стратегия' : 'Редактирование'}</h3>
        <button type="button" className="btn" onClick={onClose}>Отмена</button>
      </div>
      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Название</div>
          <input className="input" required value={name} onChange={(e) => setName(e.target.value)} style={{ width: 400 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Описание (опц.)</div>
          <input className="input" value={descr} onChange={(e) => setDescr(e.target.value)} style={{ width: 400 }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Активна</label>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>Конкуренты</strong>
            <button type="button" className="btn" onClick={() => setRules([...rules, { competitor_id: competitors[0]?.id ?? 0, coefficient: 1 }])}>+ Конкурент</button>
          </div>
          <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
            Коэффициент: <code>0.95</code> = быть на 5% дешевле конкурента, <code>1.0</code> = ровно как конкурент, <code>1.05</code> = на 5% дороже.
          </p>
          {rules.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <select className="input" value={r.competitor_id} onChange={(e) => setRules(rules.map((x, j) => j === i ? { ...x, competitor_id: Number(e.target.value) } : x))} style={{ flex: 1 }}>
                {competitors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input className="input" type="number" step="0.01" min={0.5} max={1.5} value={r.coefficient} onChange={(e) => setRules(rules.map((x, j) => j === i ? { ...x, coefficient: Number(e.target.value) } : x))} style={{ width: 100 }} />
              <button type="button" className="btn" onClick={() => setRules(rules.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          {rules.length === 0 && <div className="muted" style={{ fontSize: 13 }}>Добавьте хотя бы одного конкурента.</div>}
        </div>

        <button type="submit" className="btn primary" disabled={save.isPending || rules.length === 0 || !name}>
          {save.isPending ? 'Сохранение…' : (isNew ? 'Создать в Ozon' : 'Сохранить')}
        </button>
        {save.error && <div style={{ color: 'var(--red)', marginTop: 8, fontSize: 13 }}>{(save.error as Error).message}</div>}
      </form>
    </div>
  );
}

function StrategyProducts({ strategyId, isAdmin }: { strategyId: string; isAdmin: boolean }) {
  const qc = useQueryClient();
  const products = useQuery({ queryKey: ['ozon-strategy-products', strategyId], queryFn: () => api.ozonStrategyProducts(strategyId), refetchInterval: 15_000 });
  const allProducts = useQuery({ queryKey: ['products', 'all-500'], queryFn: () => api.products({ limit: 500 }) });

  const attachedIds = useMemo(() => new Set(products.data?.products.map((p) => String(p.product_id)) ?? []), [products.data]);

  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const add = useMutation({
    mutationFn: (ids: string[]) => api.ozonStrategyProductsAdd(strategyId, ids),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ozon-strategy-products', strategyId] }); setPicking(false); setSelected(new Set()); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.ozonStrategyProductsDelete(strategyId, [id]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ozon-strategy-products', strategyId] }),
  });

  const productById = useMemo(() => new Map(allProducts.data?.items.map((p) => [p.productId, p]) ?? []), [allProducts.data]);

  const candidates = useMemo(() => {
    if (!allProducts.data) return [];
    const q = search.toLowerCase();
    return allProducts.data.items.filter((p) => !attachedIds.has(p.productId) && (!q || p.name.toLowerCase().includes(q) || p.offerId.toLowerCase().includes(q)));
  }, [allProducts.data, attachedIds, search]);

  return (
    <div style={{ padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong>Привязанные товары ({products.data?.total ?? 0})</strong>
        {isAdmin && !picking && <button type="button" className="btn" onClick={() => setPicking(true)}>+ Привязать товары</button>}
      </div>

      {picking && (
        <div style={{ marginBottom: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 6, background: '#fff' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input className="search" placeholder="Поиск" value={search} onChange={(e) => setSearch(e.target.value)} />
            <button className="btn primary" disabled={selected.size === 0 || add.isPending} onClick={() => add.mutate(Array.from(selected))}>
              Привязать ({selected.size})
            </button>
            <button className="btn" onClick={() => { setPicking(false); setSelected(new Set()); }}>Отмена</button>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table className="products" style={{ margin: 0 }}>
              <tbody>
                {candidates.slice(0, 100).map((p) => (
                  <tr key={p.productId}>
                    <td style={{ width: 30 }}>
                      <input type="checkbox" checked={selected.has(p.productId)} onChange={(e) => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(p.productId) : next.delete(p.productId);
                        setSelected(next);
                      }} />
                    </td>
                    <td>{p.name}</td>
                    <td>{p.offerId}</td>
                    <td className="num">{p.price ? `${p.price} ₽` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {candidates.length > 100 && <div className="muted" style={{ fontSize: 12, padding: 8 }}>Показано первые 100 из {candidates.length}. Уточните поиск.</div>}
          </div>
          {add.data && add.data.failed_product_count > 0 && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>
              Не удалось привязать {add.data.failed_product_count}: {add.data.errors?.map((e) => `${e.product_id}: ${e.error}`).join('; ')}
            </div>
          )}
        </div>
      )}

      {products.data && products.data.products.length === 0 && (
        <div className="muted" style={{ fontSize: 13, padding: 12, textAlign: 'center' }}>
          К стратегии не привязано ни одного товара.
        </div>
      )}
      {products.data && products.data.products.length > 0 && (
        <table className="products" style={{ margin: 0 }}>
          <thead>
            <tr><th>Product ID</th><th>Товар</th><th>Артикул</th><th>Активен</th><th className="num">Цена strategy</th><th></th></tr>
          </thead>
          <tbody>
            {products.data.products.map((p) => {
              const info = productById.get(String(p.product_id));
              return (
                <tr key={p.product_id}>
                  <td>{p.product_id}</td>
                  <td>{info?.name ?? '—'}</td>
                  <td>{info?.offerId ?? '—'}</td>
                  <td>{p.is_enabled ? '✅' : '⏸'}</td>
                  <td className="num">{p.strategy_product_price ?? '—'} ₽</td>
                  <td>{isAdmin && <button className="btn" onClick={() => remove.mutate(String(p.product_id))}>Отвязать</button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
